"use client";

/**
 * Activities Renderer
 * Renders activity cards in the canvas (builder) and public view.
 * In builder mode, reads from the builder store with delete capability.
 * In public view, loads data via public actions and shows signup flow.
 */

import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { FetchCallout } from "@/fetch-ui/FetchCallout";

import { deleteActivity, updateActivity } from "../../../_logic/actions/activity-actions";
import { ACTIVITY_DEEPLINK_PARAM } from "../../../_logic/constants";
import { getStickyHeaderOffset } from "../../../_logic/utils/scroll-utils";
import {
  listActivitiesPublic,
  getSignupCountsPublic,
  listActivityLabelsPublic,
  listTimeSlotsForEventPublic,
  getViewerRegistrationsByEvent,
  getActiveSignupLimitRulesPublic,
} from "../../../_logic/actions/public-actions";
import { cancelMyActivityRegistrationFromSession } from "../../../_logic/actions/cancel-registration-actions";
import {
  formatSlotLabel,
  groupTimeSlotsByActivity,
  keySignupCountsByActivity,
} from "../../../_logic/utils/time-slot-utils";
import { useActivityRuleState } from "../../../_hooks/useActivityRuleState";
import { useRegisteredActivityIds } from "../../../_hooks/useRegisteredActivityIds";
import {
  describeRuleBlock,
  EMPTY_SIGNUP_LIMIT_RULES,
  isActivityPublished,
  type SignupLimitRules,
} from "../../../_logic/utils/activity-signup-status";
import { ConfirmDeleteModal } from "../../_shared/ConfirmDeleteModal";
import { ConfirmChangesModal } from "../../public/ConfirmChangesModal";
import { ActivitySignupModal } from "../../public/ActivitySignupModal";
import { usePublicActivitiesData } from "../../public/PublicActivitiesDataProvider";
import { usePublicSignupCounts } from "../../public/PublicSignupCountsProvider";
import { ExternalSlotPickerModal } from "../../public/ExternalSlotPickerModal";
import type { ActivityLabelRow, ActivityRow, ActivityTimeSlotRow, SignupCounts } from "../../../_logic/types/db-types";
import type { ActivitiesComponent } from "../../../_types";
import { ActivityCard } from "./ActivityCard";
import { useBuilderContext } from "../../../_hooks/useBuilderContext";
import { useBuilderStore } from "../../../_stores/builder-store";
import { usePublicViewContext } from "../../public/PublicViewContext";
import { useToast } from "@/components/providers/FetchToastProvider";
import { InlineTipTapEdit } from "./_shared/InlineTipTapEdit";
import { SectionContainer } from "./_shared/SectionContainer";
import { PROSE_SECTION_TEXT_VARS } from "./_shared/prose-section-text";

interface ActivitiesRendererProps {
  component: ActivitiesComponent;
  /** When true, hides delete buttons and skips store revision watching (used in public view) */
  isReadOnly?: boolean;
}

/** Viewer-relative status filter applied on top of the label filter (public view only). */
type ActivityStatusFilter = "all" | "available" | "mine";

/**
 * Client-side mirror of the server-side `test_publish` visibility gate (see
 * `public-actions.ts`'s `listActivitiesPublic`) — `test_publish` REPLACES the normal
 * publish-date gate rather than adding to it, so a viewer who can't preview test
 * content never sees it, even if `publish_at` is unset (which alone would make an
 * activity public by default). Applied both to the SSR-seeded initial state and the
 * client refetch so a stale/incorrect seed can't render unfiltered.
 */
function isVisibleToViewer(activity: ActivityRow, canPreviewTestPublish: boolean): boolean {
  return activity.test_publish ? canPreviewTestPublish : isActivityPublished(activity);
}

/**
 * Renders an activities section with cards.
 * Builder mode: preview with delete capability.
 * Public mode: full activity cards with signup buttons.
 */
export function ActivitiesRenderer({ component, isReadOnly }: ActivitiesRendererProps): React.ReactElement {
  // Hooks
  const { title, description } = component;
  const { eventId, primaryColor } = useBuilderContext();
  const {
    isPublic,
    landingPageId,
    timeZone,
    canPreviewTestPublish,
    signedUpActivityIds,
    signedUpSlotIds,
    setSignedUpActivityIds,
    setSignedUpSlotIds,
    waitlistedSlotIds,
    setWaitlistedSlotIds,
    registrationBySlotId,
    setRegistrationBySlotId,
    registrationsUnavailable,
    setRegistrationsUnavailable,
    bumpRegistrations,
  } = usePublicViewContext();
  const { showToast } = useToast();
  // Server-seeded public Activities data (activities, counts, labels, slots, rules). Null in the
  // builder canvas and when the page didn't prefetch — the load effect then fetches as a fallback.
  const activitiesSeed = usePublicActivitiesData();
  // Per-slot signup counts, prefetched event-wide so the signup modal opens without a per-click fetch.
  const signupCountsData = usePublicSignupCounts();
  const storeActivities = useBuilderStore((s) => s.activities);
  const storeActivityLabels = useBuilderStore((s) => s.activityLabels);
  const storeSignupCounts = useBuilderStore((s) => s.signupCounts);
  const storeTimeSlotsByActivity = useBuilderStore((s) => s.activityTimeSlotsByActivity);
  const activitiesStatus = useBuilderStore((s) => s.activitiesStatus);
  const loadActivitiesData = useBuilderStore((s) => s.loadActivitiesData);
  const removeActivity = useBuilderStore((s) => s.removeActivity);
  const updateActivityInStore = useBuilderStore((s) => s.updateActivityInStore);

  // States — public view data. Seeded from the server prefetch when present so the section paints
  // on first render; `publicLoaded` then short-circuits the load effect below.
  const [publicActivities, setPublicActivities] = useState<ActivityRow[]>(() =>
    (activitiesSeed?.activities ?? []).filter((a) => isVisibleToViewer(a, canPreviewTestPublish))
  );
  const [publicSignupCounts, setPublicSignupCounts] = useState<Record<number, SignupCounts>>(
    () => activitiesSeed?.signupCounts ?? {}
  );
  const [publicLabels, setPublicLabels] = useState<ActivityLabelRow[]>(() => activitiesSeed?.labels ?? []);
  const [publicTimeSlotsByActivity, setPublicTimeSlotsByActivity] = useState<Map<number, ActivityTimeSlotRow[]>>(
    () => activitiesSeed?.timeSlotsByActivity ?? new Map()
  );
  const [publicLoading, setPublicLoading] = useState(false);
  const [publicLoaded, setPublicLoaded] = useState<boolean>(() => activitiesSeed != null);
  const [signupLimitRules, setSignupLimitRules] = useState<SignupLimitRules>(
    () => activitiesSeed?.signupLimitRules ?? EMPTY_SIGNUP_LIMIT_RULES
  );

  // States — signup modal (public only)
  const [signupTarget, setSignupTarget] = useState<ActivityRow | null>(null);
  const [signupSlots, setSignupSlots] = useState<ActivityTimeSlotRow[]>([]);
  /** Guards the deep-link auto-open so it fires at most once per mount. */
  const hasAutoOpenedRef = useRef(false);

  // States — external slot picker (public only)
  const [externalPickerTarget, setExternalPickerTarget] = useState<ActivityRow | null>(null);
  const [externalPickerSlots, setExternalPickerSlots] = useState<ActivityTimeSlotRow[]>([]);

  // States — fast-path cancel confirm (public only): a single registered slot with nothing to edit.
  const [cancelTarget, setCancelTarget] = useState<{
    activity: ActivityRow;
    slotId: number;
    registrationId: number;
    slotLabel: string;
  } | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  // States — delete confirmation (builder only)
  const [deleteTarget, setDeleteTarget] = useState<ActivityRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Derived values
  const activities = isPublic ? publicActivities : storeActivities;
  const labels = isPublic ? publicLabels : storeActivityLabels;
  const signupCounts = isPublic ? publicSignupCounts : storeSignupCounts;
  const timeSlotsByActivity = isPublic ? publicTimeSlotsByActivity : storeTimeSlotsByActivity;
  const isLoading = isPublic ? publicLoading : activitiesStatus === "loading";

  // States — filters (label + viewer-relative status)
  const [activeLabelId, setActiveLabelId] = useState<number | null>(null);
  const [activeStatusFilter, setActiveStatusFilter] = useState<ActivityStatusFilter>("all");

  // Derived values — the two evaluateRuleBlock inputs that must be status-filtered to
  // `registered` only (NOT signedUpActivityIds/signedUpSlotIds, which also include
  // `waitlisted` — the day-cap/exclusion-group rule only ever counts registered rows, same as
  // the DB trigger, so including waitlisted here would falsely block a slot the server would
  // actually allow). Derived from data already in memory (no extra fetch): a slot counts as
  // "registered" when it's in signedUpSlotIds but not in waitlistedSlotIds.
  const { registeredActivityIds, registeredCountByDate } = useRegisteredActivityIds(
    timeSlotsByActivity,
    signedUpSlotIds,
    waitlistedSlotIds
  );

  // Derived values — client-side day-cap/exclusion-group hint. Hoisted once here (not per-card)
  // so no component calls a hook inside the .map() below. Builder mode never fetches
  // signupLimitRules (stays EMPTY_SIGNUP_LIMIT_RULES), so this is a no-op there.
  const ruleState = useActivityRuleState({
    activities,
    timeSlotsByActivity,
    registeredActivityIds,
    registeredCountByDate,
    rules: signupLimitRules,
  });

  // Title lookup for describeRuleBlock's conflicting-activity names — only this section has the
  // full activities list in scope (the modal itself only knows the one activity it's open for).
  const activityTitleById = useMemo(() => new Map(activities.map((a) => [a.id, a.title])), [activities]);

  // The specific "why" for the currently-open signup modal, when every bookable slot is blocked.
  // Recomputed only on modal open/target change, not per-card — the per-card CTA state only needs
  // the short ruleState.byActivityId reason, not this longer explanation.
  const signupTargetRuleBlockMessage = useMemo(() => {
    if (!signupTarget || !ruleState.byActivityId.has(signupTarget.id)) return undefined;
    const dates = (timeSlotsByActivity.get(signupTarget.id) ?? []).filter((s) => s.is_bookable).map((s) => s.date);
    return (
      describeRuleBlock(
        signupTarget.id,
        dates,
        activityTitleById,
        registeredActivityIds,
        registeredCountByDate,
        signupLimitRules
      ) ?? undefined
    );
  }, [
    signupTarget,
    ruleState.byActivityId,
    timeSlotsByActivity,
    activityTitleById,
    registeredActivityIds,
    registeredCountByDate,
    signupLimitRules,
  ]);

  // Stable identity for ActivitySignupModal's `ruleBlock` prop — an inline object literal here
  // would change identity every render and defeat the memoization inside that modal's own
  // slotStates useMemo.
  const signupTargetRuleBlock = useMemo(
    () => ({ bySlotId: ruleState.bySlotId, message: signupTargetRuleBlockMessage }),
    [ruleState.bySlotId, signupTargetRuleBlockMessage]
  );

  // Derived values — viewer signup summary + which status chips are worth showing
  const registeredCount = useMemo(
    () => activities.filter((a) => signedUpActivityIds.has(a.id)).length,
    [activities, signedUpActivityIds]
  );
  const hasAnyCapacity = useMemo(
    () => activities.some((a) => signupCounts[a.id]?.total_capacity != null),
    [activities, signupCounts]
  );
  // Only surface status chips that would actually do something for this viewer.
  const statusFilters = useMemo<{ id: ActivityStatusFilter; label: string }[]>(() => {
    const opts: { id: ActivityStatusFilter; label: string }[] = [{ id: "all", label: "All" }];
    if (hasAnyCapacity) opts.push({ id: "available", label: "Available" });
    if (registeredCount > 0) opts.push({ id: "mine", label: "My signups" });
    return opts;
  }, [hasAnyCapacity, registeredCount]);
  const showStatusFilter = isPublic && statusFilters.length > 1;

  // Derived values — filter activities by selected label and status
  const displayActivities = useMemo(() => {
    return activities.filter((a) => {
      if (activeLabelId && !a.label_ids.includes(activeLabelId)) return false;
      if (activeStatusFilter === "mine" && !signedUpActivityIds.has(a.id)) return false;
      if (activeStatusFilter === "available") {
        const counts = signupCounts[a.id];
        const total = counts?.total_capacity ?? null;
        if (total != null && (counts?.registered_count ?? 0) >= total) return false;
      }
      return true;
    });
  }, [activities, activeLabelId, activeStatusFilter, signedUpActivityIds, signupCounts]);
  const gridClass = isPublic ? "sm:grid-cols-2 lg:grid-cols-3" : "@sm:grid-cols-2 @4xl:grid-cols-3";

  // Keep the latest signed-up slot set in a ref so handleApplyChanges reads the freshest value
  // without listing signedUpSlotIds as a dep — its Set identity changes on every signup/cancel,
  // which would otherwise rebuild handleApplyChanges → onManage and re-render every ActivityCard.
  const signedUpSlotIdsRef = useRef(signedUpSlotIds);
  signedUpSlotIdsRef.current = signedUpSlotIds;

  // Same ref pattern for everything handleManage reads, so `onManage` keeps a stable identity
  // across reconciles (registrationBySlotId is a fresh Map on every signup/cancel). Without this,
  // onManage changes every interaction and defeats ActivityCard's memo — re-rendering the whole grid.
  const registrationBySlotIdRef = useRef(registrationBySlotId);
  registrationBySlotIdRef.current = registrationBySlotId;
  const publicTimeSlotsByActivityRef = useRef(publicTimeSlotsByActivity);
  publicTimeSlotsByActivityRef.current = publicTimeSlotsByActivity;
  const timeZoneRef = useRef(timeZone);
  timeZoneRef.current = timeZone;

  // Handlers
  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    const result = await deleteActivity(deleteTarget.id);
    if (result.success) {
      showToast("Activity deleted", "success");
      removeActivity(deleteTarget.id);
    } else {
      showToast(result.error || "Failed to delete activity", "error");
    }
    setIsDeleting(false);
    setDeleteTarget(null);
  }, [deleteTarget, showToast, removeActivity]);

  const handleSignupClick = useCallback((activity: ActivityRow) => {
    // Slots were prefetched on page load (listTimeSlotsForEventPublic → publicTimeSlotsByActivity)
    // and per-slot counts come from the shared PublicSignupCountsProvider, read live at render time.
    // No per-click fetch — the modal opens instantly.
    const slots = publicTimeSlotsByActivityRef.current.get(activity.id) ?? [];

    // Check if any slot has an external booking link
    const hasExternalLinks = slots.some((s) => s.booking_link);
    if (hasExternalLinks) {
      setExternalPickerTarget(activity);
      setExternalPickerSlots(slots.filter((s) => s.booking_link));
    } else {
      setSignupTarget(activity);
      setSignupSlots(slots.filter((s) => s.is_bookable));
    }
  }, []);

  const handleApplyChanges = useCallback(
    ({
      activityId,
      addedSlotIds,
      cancelledSlotIds,
    }: {
      activityId: number;
      addedSlotIds: number[];
      cancelledSlotIds: number[];
    }) => {
      // Optimistic — anonymous visitors have no server-side identity (auth.getUser() is null), so
      // the refresh below returns empty for them; updating local state first keeps the just-made
      // change visible. For session viewers it's instant feedback before the authoritative refresh.
      const nextSlots = new Set(signedUpSlotIdsRef.current);
      for (const id of addedSlotIds) nextSlots.add(id);
      for (const id of cancelledSlotIds) nextSlots.delete(id);
      setSignedUpSlotIds(nextSlots);

      if (cancelledSlotIds.length > 0) {
        setRegistrationBySlotId((prev) => {
          const next = new Map(prev);
          for (const id of cancelledSlotIds) next.delete(id);
          return next;
        });
      }

      // Activity is "registered" if any of its slots remain after the delta (or anything was added).
      const activitySlotIds = (publicTimeSlotsByActivity.get(activityId) ?? []).map((s) => s.id);
      const stillRegistered = addedSlotIds.length > 0 || activitySlotIds.some((id) => nextSlots.has(id));
      setSignedUpActivityIds((prev) => {
        const next = new Set(prev);
        if (stillRegistered) next.add(activityId);
        else next.delete(activityId);
        return next;
      });

      // Notify sections that fetch their own registration view (ScheduleRenderer) so their
      // "Registered" affordances re-sync — they don't read the context sets updated above.
      bumpRegistrations();

      // Authoritative refresh — counts + the viewer's true registrations (incl. new registration
      // ids for added slots). Union with prior so anonymous optimistic adds survive; cancellations
      // are already removed from prior and union only adds, so a cancelled slot can't reappear.
      Promise.all([getSignupCountsPublic(eventId), getViewerRegistrationsByEvent(eventId)])
        .then(([counts, viewerRegs]) => {
          setPublicSignupCounts(keySignupCountsByActivity(counts));
          setSignedUpActivityIds((prev) => {
            const next = new Set(viewerRegs.activityIds);
            for (const id of prev) next.add(id);
            return next;
          });
          setSignedUpSlotIds((prev) => {
            const next = new Set(viewerRegs.slotIds);
            for (const id of prev) next.add(id);
            return next;
          });
          // The registration map + waitlisted set are session-authoritative (empty for anonymous) —
          // set them straight.
          setWaitlistedSlotIds(viewerRegs.waitlistedSlotIds);
          setRegistrationBySlotId(viewerRegs.registrationBySlotId);
          setRegistrationsUnavailable(viewerRegs.lookupFailed);
        })
        .catch((error) => {
          console.error("ActivitiesRenderer: failed to refresh signup data", error);
          // The refresh itself never landed, so the sets above are whatever they were before —
          // flag them as unverified rather than letting a stale/empty view read as authoritative.
          setRegistrationsUnavailable(true);
        });
    },
    [
      eventId,
      publicTimeSlotsByActivity,
      setSignedUpActivityIds,
      setSignedUpSlotIds,
      setWaitlistedSlotIds,
      setRegistrationBySlotId,
      setRegistrationsUnavailable,
      bumpRegistrations,
    ]
  );

  /**
   * Card "manage/cancel" entry point. A single registered slot with no other time option is a
   * fast-path cancel (open the confirm dialog directly); anything else opens the manage modal.
   */
  const handleManage = useCallback(
    (activity: ActivityRow) => {
      const slots = publicTimeSlotsByActivityRef.current.get(activity.id) ?? [];
      const bookable = slots.filter((s) => s.is_bookable);
      const registered = bookable.filter((s) => registrationBySlotIdRef.current.has(s.id));

      if (registered.length === 1 && bookable.length <= 1) {
        const slot = registered[0];
        const registrationId = registrationBySlotIdRef.current.get(slot.id);
        if (registrationId === undefined) return;
        setCancelTarget({
          activity,
          slotId: slot.id,
          registrationId,
          slotLabel: formatSlotLabel(slot, timeZoneRef.current),
        });
        return;
      }

      handleSignupClick(activity);
    },
    [handleSignupClick]
  );

  /** Run the fast-path cancel from the confirm dialog. */
  const handleConfirmCancel = useCallback(async () => {
    if (!cancelTarget) return;
    setIsCancelling(true);
    const result = await cancelMyActivityRegistrationFromSession(cancelTarget.registrationId);
    setIsCancelling(false);
    if (result.success) {
      handleApplyChanges({
        activityId: cancelTarget.activity.id,
        addedSlotIds: [],
        cancelledSlotIds: [cancelTarget.slotId],
      });
      showToast("Registration cancelled", "success");
      setCancelTarget(null);
    } else {
      showToast(result.error || "Failed to cancel registration", "error");
    }
  }, [cancelTarget, handleApplyChanges, showToast]);

  /** Update an activity's meeting location inline (optimistic) */
  const handleUpdateLocation = useCallback(
    async (activity: ActivityRow, location: string) => {
      updateActivityInStore({ ...activity, meeting_location: location || null });

      const result = await updateActivity(activity.id, {
        meeting_location: location || undefined,
      });

      if (!result.success) {
        updateActivityInStore(activity);
        showToast(result.error || "Failed to update location", "error");
      }
    },
    [updateActivityInStore, showToast]
  );

  /** Drop the ?activity deep-link param from the URL without a navigation (keeps refresh from re-opening). */
  const clearActivityParam = useCallback(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(ACTIVITY_DEEPLINK_PARAM)) return;
    url.searchParams.delete(ACTIVITY_DEEPLINK_PARAM);
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  }, []);

  // Effects — load data
  useEffect(() => {
    if (isPublic && !publicLoaded && !publicLoading) {
      setPublicLoading(true);
      Promise.all([
        listActivitiesPublic(eventId),
        getSignupCountsPublic(eventId),
        listActivityLabelsPublic(eventId),
        listTimeSlotsForEventPublic(eventId),
        getViewerRegistrationsByEvent(eventId),
        getActiveSignupLimitRulesPublic(eventId),
      ])
        .then(([activities, counts, labels, allSlots, viewerRegs, rules]) => {
          // listActivitiesPublic already drops unpublished/non-previewable activities
          // server-side; re-apply the same gate here as a browser-side safety net so a
          // future data-path change can't surface draft/test activities in the public UI.
          setPublicActivities(activities.filter((a) => isVisibleToViewer(a, canPreviewTestPublish)));

          setPublicSignupCounts(keySignupCountsByActivity(counts));

          setPublicLabels(labels);

          setPublicTimeSlotsByActivity(groupTimeSlotsByActivity(allSlots));
          setSignedUpActivityIds(viewerRegs.activityIds);
          setSignedUpSlotIds(viewerRegs.slotIds);
          setWaitlistedSlotIds(viewerRegs.waitlistedSlotIds);
          setRegistrationBySlotId(viewerRegs.registrationBySlotId);
          setRegistrationsUnavailable(viewerRegs.lookupFailed);
          setSignupLimitRules(rules);

          setPublicLoaded(true);
          setPublicLoading(false);
        })
        .catch((error) => {
          console.error("ActivitiesRenderer: failed to load public data", error);
          setRegistrationsUnavailable(true);
          setPublicLoading(false);
        });
    } else if (!isReadOnly && activitiesStatus === "idle") {
      loadActivitiesData(eventId);
    }
  }, [
    isPublic,
    isReadOnly,
    publicLoaded,
    publicLoading,
    activitiesStatus,
    eventId,
    loadActivitiesData,
    canPreviewTestPublish,
  ]);

  // Effects — deep link: open an activity's signup flow from ?activity=<id> once data is loaded
  useEffect(() => {
    if (!isPublic || !publicLoaded || hasAutoOpenedRef.current) return;

    const raw = new URLSearchParams(window.location.search).get(ACTIVITY_DEEPLINK_PARAM);
    if (!raw) return;

    const id = Number(raw);
    if (!Number.isFinite(id)) return;

    // Silent no-op when the activity isn't in the public list (deleted, unpublished, or
    // invite-only and the viewer isn't invited — already filtered out upstream).
    const target = publicActivities.find((a) => a.id === id);
    if (!target) return;

    hasAutoOpenedRef.current = true;

    // Scroll the activities section into view *before* opening the modal so it opens in context.
    // FetchModal pins the body at whatever scroll position it captures on mount, so this must be
    // instant (not smooth): handleSignupClick mounts the modal synchronously, and a smooth scroll
    // might not have finished — leaving the visitor at the top of the page once they close.
    const sectionEl = document.getElementById(component.id);
    if (sectionEl) {
      const top = sectionEl.getBoundingClientRect().top + window.scrollY - getStickyHeaderOffset();
      window.scrollTo({ top, behavior: "auto" });
    }

    handleSignupClick(target);
  }, [isPublic, publicLoaded, publicActivities, handleSignupClick, component.id]);

  // Render
  return (
    <SectionContainer
      maxWidth="5xl"
      background={component.background}
      title={title || ""}
      componentId={component.id}
      titleFieldId="activities-title"
    >
      <InlineTipTapEdit
        content={description || ""}
        className={`prose prose-base prose-gray max-w-none prose-a:text-[var(--lp-accent)] prose-a:underline ${PROSE_SECTION_TEXT_VARS}`}
        componentId={component.id}
        fieldId="activities-description"
        field="description"
        placeholder="Click to add description..."
      />
      {isLoading ? (
        <div className={`grid gap-4 ${gridClass}`}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex animate-pulse flex-col gap-2 rounded-lg bg-gray-100 p-4">
              <div className="h-32 rounded bg-gray-200" />
              <div className="h-4 w-3/4 rounded bg-gray-200" />
              <div className="h-3 w-1/2 rounded bg-gray-200" />
            </div>
          ))}
        </div>
      ) : activities.length === 0 ? null : (
        <div className="flex flex-col gap-4">
          {/* Registration read failed — the "signed up" state on every card below is absent
              rather than false, so say so ONCE for the section instead of letting each card
              imply the viewer isn't registered. */}
          {registrationsUnavailable && activities.some((a) => a.signup_enabled) && (
            <FetchCallout variant="warning">
              We couldn&apos;t load your sign-up status. If you&apos;ve already registered, your spot is safe — check
              your confirmation email.
            </FetchCallout>
          )}

          {/* Viewer signup summary — only for a known viewer with at least one registration */}
          {registeredCount > 0 && (
            <p className="text-center text-sm font-medium text-[color:var(--lp-text,#4b5563)]" aria-live="polite">
              You&apos;re registered for {registeredCount} of {activities.length}{" "}
              {activities.length === 1 ? "activity" : "activities"}
            </p>
          )}

          {/* Status filter chips (public view only). Inactive chips sit straight on the section
              background, so their text follows --lp-text and the hover pill follows
              --lp-surface-hover — both fall back to the original gray/gray-100 pair, so a page
              with no override keeps exactly the affordance it had. */}
          {showStatusFilter && (
            <div
              className="flex flex-wrap items-center justify-center gap-1"
              role="radiogroup"
              aria-label="Filter by availability"
            >
              {statusFilters.map(({ id, label }) => {
                const isActive = activeStatusFilter === id;
                return (
                  <button
                    type="button"
                    role="radio"
                    key={id}
                    aria-checked={isActive}
                    onClick={() => setActiveStatusFilter(id)}
                    className={`shrink-0 touch-manipulation rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                      isActive
                        ? ""
                        : "text-[color:var(--lp-text,#4b5563)] hover:bg-[color:var(--lp-surface-hover,#f3f4f6)]"
                    }`}
                    style={isActive ? { backgroundColor: `${primaryColor}1a`, color: primaryColor } : undefined}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Label filter tabs */}
          {labels.length > 0 && (
            <div
              className="flex flex-wrap items-center justify-center gap-1"
              role="radiogroup"
              aria-label="Filter by label"
            >
              <button
                type="button"
                role="radio"
                aria-checked={activeLabelId === null}
                onClick={() => setActiveLabelId(null)}
                className={`shrink-0 touch-manipulation rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                  activeLabelId === null
                    ? ""
                    : "text-[color:var(--lp-text,#4b5563)] hover:bg-[color:var(--lp-surface-hover,#f3f4f6)]"
                }`}
                style={
                  activeLabelId === null ? { backgroundColor: `${primaryColor}1a`, color: primaryColor } : undefined
                }
              >
                All
              </button>
              {labels.map((label) => {
                const labelColor = label.color || "#6366f1";
                const isActive = activeLabelId === label.id;
                return (
                  <button
                    type="button"
                    role="radio"
                    key={label.id}
                    aria-checked={isActive}
                    onClick={() => setActiveLabelId(label.id)}
                    className={`shrink-0 touch-manipulation rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                      isActive
                        ? ""
                        : "text-[color:var(--lp-text,#4b5563)] hover:bg-[color:var(--lp-surface-hover,#f3f4f6)]"
                    }`}
                    style={isActive ? { backgroundColor: `${labelColor}20`, color: labelColor } : undefined}
                  >
                    {label.name}
                  </button>
                );
              })}
            </div>
          )}

          {displayActivities.length === 0 ? (
            <p className="py-8 text-center text-sm font-medium text-[color:var(--lp-text,#6b7280)]">
              No activities match this filter.
            </p>
          ) : (
            <div className={`grid gap-4 ${gridClass}`}>
              {displayActivities.map((activity) => (
                <ActivityCard
                  key={activity.id}
                  activity={activity}
                  counts={signupCounts[activity.id]}
                  labels={labels}
                  timeSlots={timeSlotsByActivity.get(activity.id) ?? EMPTY_SLOTS}
                  isReadOnly={isReadOnly}
                  componentId={component.id}
                  onDelete={setDeleteTarget}
                  onSignup={handleSignupClick}
                  onManage={handleManage}
                  onUpdateLocation={handleUpdateLocation}
                  ruleBlock={ruleState.byActivityId.get(activity.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal (builder only) */}
      {!isReadOnly && (
        <ConfirmDeleteModal
          isOpen={deleteTarget !== null}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          title="Delete Activity"
          description={`Delete "${deleteTarget?.title}"? This will also delete all signups.`}
          isDeleting={isDeleting}
        />
      )}

      {/* Signup Modal (public only — internal signups) */}
      {signupTarget && (
        <ActivitySignupModal
          activity={signupTarget}
          timeSlots={signupSlots}
          slotCounts={signupCountsData?.slotCounts ?? {}}
          primaryColor={primaryColor}
          landingPageId={landingPageId}
          ruleBlock={signupTargetRuleBlock}
          onClose={() => {
            setSignupTarget(null);
            clearActivityParam();
          }}
          onSuccess={handleApplyChanges}
        />
      )}

      {/* External Slot Picker Modal (public only — external bookings) */}
      {externalPickerTarget && (
        <ExternalSlotPickerModal
          activity={externalPickerTarget}
          timeSlots={externalPickerSlots}
          primaryColor={primaryColor}
          onClose={() => {
            setExternalPickerTarget(null);
            clearActivityParam();
          }}
        />
      )}

      {/* Fast-path cancel confirm (public only — single registration, nothing to edit) */}
      {cancelTarget && (
        <ConfirmChangesModal
          activityTitle={cancelTarget.activity.title}
          toAdd={EMPTY_LABELS}
          toCancel={[cancelTarget.slotLabel]}
          primaryColor={primaryColor}
          isSubmitting={isCancelling}
          onBack={() => setCancelTarget(null)}
          onConfirm={handleConfirmCancel}
        />
      )}
    </SectionContainer>
  );
}

/** Stable empty array for the confirm dialog's add list (fast-path cancel has no additions). */
const EMPTY_LABELS: string[] = [];

/** Stable empty array to avoid re-creating on every render */
const EMPTY_SLOTS: ActivityTimeSlotRow[] = [];
